import { queryClient } from '@/query-client';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import {
  type Source,
  SourceSchema,
  type Sources,
  type SourcesQuery,
  SourcesSchema,
  type SourcesSearchQuery,
  type SourcesSearchResults,
  SourcesSearchResultsSchema,
  type UpdateSource,
} from '@mai/types';

import { apiClient } from './axios';

import { logger } from '@utils/logger';

export const useSourcesQuery = (params: SourcesQuery, enabled: boolean) => {
  return useQuery({
    queryKey: ['sources', params],
    queryFn: async () => {
      const response = await apiClient.get<Sources>('/sources', { params });
      const validatedData = SourcesSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate sources data', {
          error: validatedData.error,
        });
      }
      return validatedData.data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
};

export const useInfiniteSourcesQuery = (
  params: SourcesQuery,
  enabled: boolean,
) => {
  return useInfiniteQuery({
    queryKey: ['sources', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.get<Sources>('/sources', {
        params: { ...params, page: pageParam },
      });
      const validatedData = SourcesSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate sources data', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    getNextPageParam: (_, pages) => pages.length + 1,
    initialPageParam: 0,
    enabled,
  });
};

export const useSourceQuery = (sourceId: string | undefined | null) => {
  return useQuery({
    queryKey: ['source', sourceId],
    queryFn: async () => {
      const response = await apiClient.get<Source>(`/sources/${sourceId}`);
      const validatedData = SourceSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate source data', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled: !!sourceId,
  });
};

export const useSourcesSearchQuery = (
  params: SourcesSearchQuery,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ['sources', 'search', params],
    queryFn: async () => {
      const response = await apiClient.get<SourcesSearchResults>(
        '/sources/search',
        { params },
      );
      const validatedData = SourcesSearchResultsSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate sources search results data', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled,
  });
};

export const updateSource = async (sourceId: string, body: UpdateSource) => {
  const response = await apiClient.patch<Source>(`/sources/${sourceId}`, body);
  const validatedData = SourceSchema.safeParse(response.data);
  if (!validatedData.success) {
    logger.warn('failed to validate source data', {
      error: validatedData.error,
    });
  }
  await queryClient.invalidateQueries({ queryKey: ['source', sourceId] });
  return validatedData.data;
};
